import React, { useState, useEffect, memo } from 'react';
import Select from 'react-dropdown-select';
import { User, Users, Check, X, AlertCircle, Clock } from 'lucide-react';
import ScanBadgeButton from '../../components/scan-badge-button/scan-badge-button';
import workshops from '../../assets/workshopList';
import {
  fetchUserRecord,
  updateWorkshopList,
} from '../../utils/mongoDataOperations';

// Memoized clock component to prevent unnecessary re-renders
const LiveClock = memo(() => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    // Update every second
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    // Cleanup interval on unmount
    return () => clearInterval(timer);
  }, []);

  return (
    <div className='flex items-center mb-4 space-x-2 text-gray-600'>
      <Clock className='h-5 w-5' />
      <span className='text-lg'>
        {time.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}
      </span>
    </div>
  );
});

LiveClock.displayName = 'LiveClock';

const WorkshopsPage = () => {
  const [workshopId, setWorkshopId] = useState(null);
  const [scannedReturnedID, setScannedReturnedID] = useState(null);
  const [scannedUserRecord, setScannedUserRecord] = useState(null);
  const [workshopAlreadyMarked, setWorkshopAlreadyMarked] = useState(false);

  // Function to check if a workshop is past its end time
  const isPastWorkshop = (endTime) => {
    const now = new Date();
    return new Date(endTime) < now;
  };

  // Transform workshops data to include disabled state
  const processedWorkshops = workshops.map((workshop) => ({
    ...workshop,
    disabled: isPastWorkshop(workshop.endTime),
  }));

  const customDropdownRenderer = ({ props, state, methods }) => {
    const options = props.options || [];
    return (
      <div className='relative w-full'>
        {options.map((option) => {
          const isDisabled = option.disabled;
          const isSelected = state.values?.some(
            (value) => value.value === option.value
          );
          return (
            <div
              key={option.value}
              className={`px-4 py-2 cursor-pointer ${
                isDisabled
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'hover:bg-blue-50'
              } ${isSelected ? 'bg-blue-100' : ''}`}
              onClick={() => !isDisabled && methods.addItem(option)}>
              {option.label}
            </div>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      if (scannedReturnedID) {
        try {
          const response = await fetchUserRecord(scannedReturnedID);
          setScannedUserRecord(response);

          const workshopAlreadyMarked =
            response?.workshop_info?.attendedWorkshops.includes(workshopId);

          setWorkshopAlreadyMarked(workshopAlreadyMarked);

          if (!workshopAlreadyMarked && workshopId) {
            const updatedWorkshopList = [
              ...(response?.workshop_info?.attendedWorkshops || []),
              workshopId,
            ];

            updateWorkshopList(scannedReturnedID, updatedWorkshopList);
          }
        } catch (error) {
          console.error(
            'Error fetching user record or updating workshop:',
            error
          );
        }
      }
    };
    fetchData();
  }, [scannedReturnedID, workshopId]);

  const handleWorkshopSelect = (values) => {
    const selected_workshop = values[0];
    if (!selected_workshop || selected_workshop.disabled) {
      return;
    }

    const selected_workshop_id = selected_workshop.workshop_id;
    if (selected_workshop_id === workshopId) {
      setWorkshopId(null);
    } else {
      setWorkshopId(selected_workshop_id);
    }
    // Reset user data when workshop changes
    setScannedUserRecord(null);
    setScannedReturnedID(null);
    setWorkshopAlreadyMarked(false);
  };

  const Card = ({ title, children, className }) => (
    <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
      {title && <h2 className='font-semibold mb-4 text-xl'>{title}</h2>}
      {children}
    </div>
  );

  const InfoItem = ({ icon: Icon, label, value }) => (
    <div className='flex items-center mb-4 space-x-3'>
      <div className='bg-gray-100 flex h-8 items-center justify-center rounded-full w-8'>
        <Icon className='h-4 text-gray-600 w-4' />
      </div>
      <div>
        <p className='text-gray-500 text-sm'>{label}</p>
        <p className='font-medium'>{value || '...'}</p>
      </div>
    </div>
  );

  const getAttendanceStatus = () => {
    if (!workshopId) {
      return {
        icon: AlertCircle,
        bgColor: 'bg-yellow-100',
        iconBgColor: 'bg-yellow-200',
        iconColor: 'text-yellow-600',
        title: 'Please Select Workshop',
        message: 'Select a workshop before scanning participant badges',
      };
    }

    if (scannedUserRecord && !scannedUserRecord?.check_in_info?.checkedIn) {
      return {
        icon: X,
        bgColor: 'bg-red-100',
        iconBgColor: 'bg-red-200',
        iconColor: 'text-red-600',
        title: 'Participant Not Checked In',
        message:
          'This participant needs to check in before attending workshops',
      };
    }

    if (workshopAlreadyMarked) {
      return {
        icon: X,
        bgColor: 'bg-red-100',
        iconBgColor: 'bg-red-200',
        iconColor: 'text-red-600',
        title: 'Already Scanned',
        message: 'This participant has already been marked for this workshop',
      };
    }

    return {
      icon: Check,
      bgColor: 'bg-green-100',
      iconBgColor: 'bg-green-200',
      iconColor: 'text-green-600',
      title: 'Marked Attendance',
      message: 'Successfully recorded workshop attendance',
    };
  };

  return (
    <div className='max-w-4xl mx-auto p-6 space-y-6'>
      <LiveClock />
      <div className='flex items-center justify-between mb-6'>
        <h1 className='font-bold text-3xl'>Workshop Attendance</h1>
        <div className='flex items-center space-x-3'>
          <button
            onClick={() => {
              setScannedUserRecord(null);
              setScannedReturnedID(null);
              setWorkshopAlreadyMarked(false);
              setWorkshopId(null);
            }}
            className='border border-gray-300 hover:bg-gray-50 inline-flex items-center px-4 py-2 rounded-lg text-gray-700 transition-colors'>
            Clear
          </button>
          <ScanBadgeButton
            scannedReturnedID={scannedReturnedID}
            setScannedReturnedID={setScannedReturnedID}
            disabled={!workshopId}
          />
        </div>
      </div>

      <div className='max-w-xl mb-8 mx-auto w-full'>
        <Select
          className='font-roboto'
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            fontSize: '1.2rem',
          }}
          options={processedWorkshops}
          values={
            workshopId
              ? processedWorkshops.filter(
                  (workshop) => workshop.workshop_id === workshopId
                )
              : []
          }
          onChange={handleWorkshopSelect}
          placeholder='Select a workshop...'
          dropdownRenderer={customDropdownRenderer}
        />
      </div>

      {scannedUserRecord && (
        <div className='gap-6 grid grid-cols-1 md:grid-cols-2'>
          <Card title='Hacker Info'>
            <InfoItem
              icon={User}
              label='Full Name'
              value={scannedUserRecord?.application_info?.full_name}
            />
            <InfoItem
              icon={Users}
              label='Gender'
              value={scannedUserRecord?.application_info?.gender}
            />
          </Card>

          <Card title='Attendance Status'>
            {(() => {
              const status = getAttendanceStatus();
              const StatusIcon = status.icon;

              return (
                <div
                  className={`p-4 rounded-lg flex items-center space-x-3 ${status.bgColor}`}>
                  <div className={`rounded-full p-2 ${status.iconBgColor}`}>
                    <StatusIcon className={`h-6 w-6 ${status.iconColor}`} />
                  </div>
                  <div>
                    <p className={`font-medium ${status.iconColor}`}>
                      {status.title}
                    </p>
                    <p className='text-gray-600 text-sm'>{status.message}</p>
                  </div>
                </div>
              );
            })()}
          </Card>
        </div>
      )}
    </div>
  );
};

export default WorkshopsPage;
